// ABOUTME: Orientation lock (SPEC-26 Phase 5) — the rule for which orientation each device class is held to.
// ABOUTME: Pure decision + a best-effort native Screen Orientation lock; the UI lives in OrientationGate.

// Client requirement, 2026-08-13: a handset is portrait-only and a tablet is landscape-only, so that
// every device sees a composition at the shape it was authored for. It follows from the Figma file
// having exactly two frames and no tablet one:
//
//   • mobile  1216x2160 — portrait. A phone rotated sideways crosses into the desktop composition,
//     whose 1138-authored-px rail was drawn for a 3840-wide frame, on a 852px screen.
//   • desktop 3840x2160 — landscape, and what a tablet now gets (client direction: same icons, same
//     rail, same layout as desktop — no hamburger). A tablet held upright would be showing a 16:9
//     composition in a 3:4 window.
//
// The web platform cannot forbid rotation in a browser tab, so this is two mechanisms, strongest
// first, and the spec asks for both:
//
//   1. `screen.orientation.lock(...)` — a real lock, honoured when the document is fullscreen or the
//      app is installed (`display-mode: standalone`). Rejects in an ordinary tab, and is not
//      implemented at all on iOS Safari.
//   2. A blocking overlay that refuses the wrong orientation (`OrientationGate`). This is the one
//      that always runs, and on an iPhone it is the only one there is.
//
// The lock is applied at runtime per device class rather than declared in `manifest.json`, because a
// manifest orientation applies to every installed client at once and the two classes now want
// opposite things. See §0g.
//
// The rule lives here, apart from the component, because the interesting part is *who gets locked*
// and that has to be testable against a table of real devices rather than through a render. It is
// deliberately conservative: a false positive locks a guest out of the app entirely, with no way
// through, so every clause below is about not blocking a device that cannot rotate.
//
// AC-10: nothing brand-, chapter- or product-specific appears in this file.

/**
 * The largest short-side, in CSS px, that still reads as a phone.
 *
 * A phone's short side is its portrait width and, near enough, its landscape height: 375-430 on
 * every iPhone, 360-412 on mainstream Android. A tablet's is 744 (iPad mini) at the very smallest.
 * 540 sits in the gap with room either side, and it is the same measurement in both orientations,
 * so the classification does not change under the very rotation it is deciding about.
 */
export const PHONE_MAX_MIN_SIDE = 540;

/**
 * The largest long-side that still reads as a tablet, rather than a large touch display.
 *
 * iPad Pro 12.9 — the biggest tablet anyone will bring to a demo — is 1366 in landscape. Above 1600
 * we are looking at a touchscreen monitor or a kiosk panel, which is a fixed installation: it cannot
 * be turned over, so it must never be asked to. Same reasoning as the desktop clause below, one size
 * class up.
 */
export const TABLET_MAX_MAX_SIDE = 1600;

/** Landscape by shape, not by `screen.orientation.type` — a square window is not landscape, and the
 *  layout viewport is what the composition is actually laid out against. */
export function isLandscape(viewport) {
  if (!viewport) return false;
  const { width, height } = viewport;
  if (!width || !height) return false;
  return width > height;
}

/**
 * Something the guest is holding, as opposed to something sitting on a desk.
 *
 * A mobile UA, or a coarse primary pointer with no hover. A touchscreen laptop with a mouse plugged
 * in reports a *fine* pointer and real hover, so it fails this test even when its window is dragged
 * to a phone-ish shape — which is the case worth protecting, because it cannot be rotated.
 */
function isHandheld(profile) {
  if (!profile) return false;
  return Boolean(profile.mobileUA || (profile.coarsePointer && profile.noHover));
}

/**
 * The size to classify by — the physical screen where the browser reports one, the layout viewport
 * otherwise.
 *
 * **Phone versus tablet cannot be answered by capability.** They are identical on every signal
 * `deviceProfile` collects — coarse pointer, no hover, touch points — and the UA is worse than
 * useless (iPadOS 13+ claims to be a Mac). Size is the only discriminator, and the gap it has to
 * straddle is wide and stable: every phone's short side is 320-430 CSS px, every tablet's is
 * 744-1024. `PHONE_MAX_MIN_SIDE` sits in ~300px of clear air between them.
 *
 * The *screen* rather than the viewport, because a window is not a device: iPad Split View hands the
 * browser a ~507px-wide window on an 834px-wide tablet, and classifying that as a phone would ask an
 * iPad to rotate to portrait. Falls back to the viewport when `screen` is unreadable, which keeps
 * this pure-testable and covers SSR.
 */
function sides(viewport, profile) {
  if (profile && profile.screenShortSide > 0 && profile.screenLongSide > 0) {
    return { min: profile.screenShortSide, max: profile.screenLongSide };
  }
  if (!viewport) return null;
  const { width, height } = viewport;
  if (!width || !height) return null;
  return { min: Math.min(width, height), max: Math.max(width, height) };
}

/** A handheld with a phone-sized short side. Held to portrait. */
export function isPhoneClass(viewport, profile) {
  const side = sides(viewport, profile);
  if (!side) return false;
  if (side.min > PHONE_MAX_MIN_SIDE) return false;
  return isHandheld(profile);
}

/** A handheld bigger than a phone and smaller than a fixed display. Held to landscape. */
export function isTabletClass(viewport, profile) {
  const side = sides(viewport, profile);
  if (!side) return false;
  if (side.min <= PHONE_MAX_MIN_SIDE) return false;
  if (side.max > TABLET_MAX_MAX_SIDE) return false;
  return isHandheld(profile);
}

/**
 * The orientation this viewport is being held to, or `null` for "whatever you like".
 *
 * `null` for every desktop, at any window shape and any zoom level: a client that cannot be rotated
 * must never be shown a prompt asking it to be.
 *
 * @param {{ viewport: {width:number, height:number}, profile: object, enabled?: boolean }} input
 * @returns {'portrait'|'landscape'|null}
 */
export function requiredOrientation({ viewport, profile, enabled = true }) {
  if (!enabled) return null;
  if (isPhoneClass(viewport, profile)) return 'portrait';
  if (isTabletClass(viewport, profile)) return 'landscape';
  return null;
}

/**
 * Should the rotate prompt be showing right now, and which way?
 *
 * @returns {'portrait'|'landscape'|null} the orientation to ask for, or null if nothing is wrong.
 */
export function orientationToPromptFor({ viewport, profile, enabled = true }) {
  const required = requiredOrientation({ viewport, profile, enabled });
  if (!required) return null;
  const landscape = isLandscape(viewport);
  if (required === 'portrait') return landscape ? 'portrait' : null;
  return landscape ? null : 'landscape';
}

/** The `ScreenOrientation` object, or null where the API is absent (iOS Safari, jsdom, SSR). */
function screenOrientation() {
  if (typeof window === 'undefined') return null;
  const target = window.screen && window.screen.orientation;
  return target && typeof target.lock === 'function' ? target : null;
}

/**
 * Ask the platform for a real lock. Never rejects and never throws: not being allowed to lock is the
 * normal case (a browser tab that is not fullscreen), not an error — the overlay is what carries the
 * requirement, and this is the upgrade for the contexts that permit it.
 *
 * Chromium rejects with a `NotSupportedError`/`SecurityError` DOMException; older engines throw
 * synchronously instead of returning a promise at all, hence both guards.
 *
 * @param {'portrait'|'landscape'} orientation
 * @returns {Promise<boolean>} whether the platform actually took the lock.
 */
export function requestOrientationLock(orientation) {
  const target = screenOrientation();
  if (!target) return Promise.resolve(false);
  try {
    const result = target.lock(orientation);
    if (result && typeof result.then === 'function') {
      return result.then(() => true, () => false);
    }
    return Promise.resolve(true);
  } catch {
    return Promise.resolve(false);
  }
}

/** Undo `requestOrientationLock`. Only call it for a lock this document actually took — `unlock()`
 *  returns the screen to the document's default orientation, which is not necessarily free. */
export function releaseOrientationLock() {
  const target = screenOrientation();
  if (!target || typeof target.unlock !== 'function') return;
  try {
    target.unlock();
  } catch {
    /* Same story as lock(): refusing to unlock is not an error worth surfacing. */
  }
}
